import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  apiRequest,
  apiRequestBlob,
  apiRequestUpload,
  apiRequestUploadWithProgress,
  apiRequestWithStatus,
  ApiError,
  type UploadProgressEvent,
} from "@/lib/api";
import {
  getMe,
  getSecurityStatus,
  logout as logoutRequest,
  refresh,
  type AuthTokens,
  type BrokerIdentity,
  type BrokerSecurityPosture,
  type AuthedBlobRequester,
  type AuthedUploader,
  type AuthedProgressUploader,
  type AuthedRequesterWithStatus,
  type AuthedRequestOptions,
} from "@/lib/brokerAuth";
import {
  authReducer,
  initialAuthState,
  type AuthStatus,
} from "@/auth/authReducer";

// Broker auth context. Holds the session in memory (CLAUDE.md #5/#9) and
// exposes the few operations the UI needs:
//   - on mount: silently restore a session from the httpOnly refresh cookie;
//   - completeLogin: after /login (or /set-password) hands back tokens, resolve
//     the broker identity via /api/broker/me and go authenticated;
//   - signOut: revoke + clear server-side, then drop the in-memory session.
interface AuthContextValue {
  status: AuthStatus;
  identity: BrokerIdentity | null;
  /**
   * Make an authenticated /api/broker/** request: injects the in-memory access
   * token and, on a 401, silently refreshes via the httpOnly cookie and retries
   * once. A failed refresh drops the session to anonymous. Broker-guarded calls
   * (MFA enrollment, and future broker features) go through this.
   */
  authedRequest: AuthedRequesterWithStatus;
  /** Make an authenticated request for BYTES rather than JSON — the indication
   *  letter, and nothing else so far (BRK-S133). Same 401-refresh-retry. */
  authedBlob: AuthedBlobRequester;
  /** A FILE POST through the same wrapper (BRK-S141). */
  authedUpload: AuthedUploader;
  /** A FILE POST that reports upload progress (BRK-S145) — a broker attaching
   *  a 9 MB scan to a bond order watches it move. Same 401-refresh-retry. */
  authedUploadWithProgress: AuthedProgressUploader;
  /** Promote freshly-issued tokens to an authenticated session (fetches /me). */
  completeLogin: (tokens: AuthTokens) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * The broker's security posture (PMS-S073), fetched from
   * GET /api/broker/security-status once authenticated. `null` until the first
   * read resolves, or if it fails / the PMS doesn't serve it (the fail-safe in
   * `hasStrongFactor` then declines to prompt). Drives the guided onboarding
   * nudge (BRK-S026) and the /security password card.
   */
  posture: BrokerSecurityPosture | null;
  /** False until the first posture read for this session settles — the gate
   *  shows a brief loading state instead of flashing the page or the prompt. */
  postureReady: boolean;
  /** Re-read the posture after the broker changes it (set a password, register a
   *  passkey, enroll/disable MFA) so the nudge + cards re-evaluate. */
  refreshPosture: () => Promise<void>;
  /**
   * Whether the broker has dismissed the "secure your account" prompt for this
   * session ("I'll do this later"). In-memory only, so it resets on a fresh
   * login/reload — the nudge returns next session until a strong factor exists
   * (BRK-S026).
   */
  securityPromptDismissed: boolean;
  /** Dismiss the security prompt for the rest of this session. */
  dismissSecurityPrompt: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const [securityPromptDismissed, setSecurityPromptDismissed] = useState(false);
  const [posture, setPosture] = useState<BrokerSecurityPosture | null>(null);
  const [postureReady, setPostureReady] = useState(false);

  // Mirror the session in refs so authedRequest reads the latest token/identity
  // without a stale closure (and without re-creating the callback per render).
  //
  // Every place that establishes or drops a session sets the refs itself,
  // BEFORE dispatching (BRK-S260). React runs a child's effects before its
  // parent's, so the shell's mount-time reads ran ahead of this effect's sync
  // and went out with no token: a 401 each, and a refresh each to recover.
  // The effect stays as a backstop.
  const tokensRef = useRef(state.tokens);
  const identityRef = useRef(state.identity);
  useEffect(() => {
    tokensRef.current = state.tokens;
    identityRef.current = state.identity;
  }, [state.tokens, state.identity]);

  /** The session is here: the refs first, then the render that shows it. */
  const establish = useCallback(
    (tokens: AuthTokens, identity: BrokerIdentity) => {
      tokensRef.current = tokens;
      identityRef.current = identity;
      dispatch({ type: "authenticated", tokens, identity });
    },
    [],
  );

  /** The session is gone, from the refs as well as the state. */
  const drop = useCallback(() => {
    tokensRef.current = null;
    identityRef.current = null;
    dispatch({ type: "anonymous" });
  }, []);

  // Exchange the httpOnly refresh cookie for a fresh access token, keeping the
  // existing identity. On failure the session goes anonymous (the guard then
  // routes to /login) and the error propagates.
  const refreshAccessToken = useCallback(async (): Promise<string> => {
    try {
      const tokens = await refresh();
      const identity = identityRef.current;
      if (!identity) throw new Error("No identity to refresh");
      establish(tokens, identity); // the immediate retry sees the new token
      return tokens.accessToken;
    } catch (err) {
      drop();
      setPosture(null);
      setPostureReady(false);
      throw err;
    }
  }, [establish, drop]);

  // Wrap any access-token-using call with the 401-refresh-retry: run with the
  // current token; on a 401 refresh once via the cookie and retry; anything else
  // (or a second 401) propagates. Shared by authedRequest and its .withStatus.
  const runAuthed = useCallback(
    async <R,>(call: (token: string | null) => Promise<R>): Promise<R> => {
      try {
        return await call(tokensRef.current?.accessToken ?? null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          const fresh = await refreshAccessToken();
          return await call(fresh);
        }
        throw err;
      }
    },
    [refreshAccessToken],
  );

  const authedRequest = useMemo<AuthedRequesterWithStatus>(() => {
    const fn = (<T,>(path: string, opts: AuthedRequestOptions = {}) =>
      runAuthed((token) =>
        apiRequest<T>(path, { ...opts, accessToken: token }),
      )) as AuthedRequesterWithStatus;
    fn.withStatus = <T,>(path: string, opts: AuthedRequestOptions = {}) =>
      runAuthed((token) =>
        apiRequestWithStatus<T>(path, { ...opts, accessToken: token }),
      );
    return fn;
  }, [runAuthed]);

  /**
   * The BYTES read (BRK-S133), through the SAME `runAuthed` wrapper the JSON
   * requester uses — so a broker whose access token expired while they were
   * reading their indication gets a silent refresh and a retry, not a 401 and
   * an empty frame.
   */
  const authedBlob = useMemo<AuthedBlobRequester>(
    () => (path: string) =>
      runAuthed((token) => apiRequestBlob(path, { accessToken: token })),
    [runAuthed],
  );

  /**
   * The FILE write (BRK-S141), through the same `runAuthed` wrapper — so a
   * broker whose token expired while they were picking a PDF gets a silent
   * refresh and a retry rather than a 401 on a file they now have to choose
   * again.
   */
  const authedUpload = useMemo<AuthedUploader>(
    () =>
      (<T,>(path: string, form: FormData) =>
        runAuthed((token) =>
          apiRequestUpload<T>(path, form, { accessToken: token }),
        )) as AuthedUploader,
    [runAuthed],
  );

  /**
   * The same write, with PROGRESS (BRK-S145).
   *
   * Its own requester rather than an option on the one above, because the
   * transport differs: progress needs XHR, and one function that sometimes
   * reports it would be a promise the `fetch` path cannot keep. The retry story
   * is identical — a refresh replays the whole upload, which is correct: the
   * bytes never landed.
   */
  const authedUploadWithProgress = useMemo<AuthedProgressUploader>(
    () =>
      (<T,>(
        path: string,
        form: FormData,
        options?: {
          onProgress?: (p: UploadProgressEvent) => void;
          signal?: AbortSignal;
        },
      ) =>
        runAuthed((token) =>
          apiRequestUploadWithProgress<T>(path, form, {
            accessToken: token,
            onProgress: options?.onProgress,
            signal: options?.signal,
          }),
        )) as AuthedProgressUploader,
    [runAuthed],
  );

  const completeLogin = useCallback(
    async (tokens: AuthTokens) => {
      // /me confirms the token is valid AND the broker login is enabled; if it
      // isn't (403) the error propagates so the caller can show it and we stay
      // anonymous rather than render a half-authenticated shell.
      const identity = await getMe(tokens.accessToken);
      // A fresh sign-in re-arms the security prompt (a returning unprotected broker
      // is nudged again this session, BRK-S026).
      setSecurityPromptDismissed(false);
      establish(tokens, identity);
    },
    [establish],
  );

  // Read the broker's security posture (PMS-S073) via the authed requester (so it
  // inherits refresh-on-401, BRK-S015). A failed read leaves posture null — the
  // fail-safe in hasStrongFactor then declines to prompt rather than trap anyone.
  const refreshPosture = useCallback(async () => {
    try {
      setPosture(await getSecurityStatus(authedRequest));
    } catch {
      setPosture(null);
    } finally {
      setPostureReady(true);
    }
  }, [authedRequest]);

  const dismissSecurityPrompt = useCallback(() => {
    setSecurityPromptDismissed(true);
  }, []);

  // Load the posture once a session is authenticated. Runs in an effect (not
  // inline in completeLogin) so the token refs the authed requester reads are
  // already current. The reset on sign-out / a failed refresh is done where those
  // happen, not in an else branch here, to avoid an extra render on the anonymous
  // transition (it would race the post-sign-out redirect).
  useEffect(() => {
    if (state.status === "authenticated") void refreshPosture();
  }, [state.status, refreshPosture]);

  const signOut = useCallback(async () => {
    // Logout is best-effort server-side (revoke + clear cookie); a failure there
    // must never block the user from signing out, so swallow it and always drop
    // the in-memory session.
    try {
      await logoutRequest();
    } catch {
      // ignore — the local session clears regardless.
    }
    setSecurityPromptDismissed(false);
    setPosture(null);
    setPostureReady(false);
    drop();
  }, [drop]);

  // Silent session restore on first load. A missing/expired cookie just lands
  // us at "anonymous" — no error surfaced, the splash/login simply shows.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokens = await refresh();
        const identity = await getMe(tokens.accessToken);
        if (!cancelled) establish(tokens, identity);
      } catch {
        if (!cancelled) drop();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [establish, drop]);

  return (
    <AuthContext.Provider
      value={{
        status: state.status,
        identity: state.identity,
        authedRequest,
        authedBlob,
        authedUpload,
        authedUploadWithProgress,
        completeLogin,
        signOut,
        posture,
        postureReady,
        refreshPosture,
        securityPromptDismissed,
        dismissSecurityPrompt,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Co-located with the provider by design (the hook is the provider's public
// surface); the rule only concerns HMR fast-refresh, not correctness.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
